import { z } from 'zod'

import { nonEmptyArraySchema } from '@mntn-dev/utilities'
import type { ZodInfer } from '@mntn-dev/utility-types'

import { ProjectStatusSchema } from '../models/project.models.ts'
import { NonEmptyStringSchema } from '../models/property.models.ts'
import { ProjectDomainQueryModelSchema } from '../models/query.models.ts'
import { OrganizationIdSchema } from '../models/unique-id.models.ts'

export const ProjectListItemServiceModelSchema =
  ProjectDomainQueryModelSchema.pick({
    projectId: true,
    projectKey: true,
    name: true,
    description: true,
    dueDate: true,
    ownerId: true,
    brandTeamId: true,
    brandCompanyName: true,
    acceptorId: true,
    agencyTeamId: true,
    status: true,
    thumbnailFileId: true,
    lastTouchedTimestamp: true,
    cost: true,
    costPlusMargin: true,
    packageId: true,
    billingSchedule: true,
    invoiceTerms: true,
    billingMethods: true,
    chosenBillingMethod: true,
    currency: true,
    bids: true,
    biddingCloseDate: true,
    brandTeam: true,
    package: true,
  })
    .extend({
      inherited: ProjectDomainQueryModelSchema.shape.inherited.extend({
        package: ProjectDomainQueryModelSchema.shape.inherited.shape.package
          .pick({
            name: true,
            cost: true,
            costPlusMargin: true,
            source: true,
          })
          .partial({ cost: true, costPlusMargin: true }),
      }),
      acl: z.object({
        canArchiveProject: z.boolean(),
        canCreateProjectBids: z.boolean(),
        canDuplicateProject: z.boolean(),
        canSetProjectClosed: z.boolean(),
      }),
    })
    .partial({
      cost: true,
      costPlusMargin: true,
    })

export type ProjectListItemServiceModel = ZodInfer<
  typeof ProjectListItemServiceModelSchema
>

const ProjectListAclSchema = z.object({
  canViewCostPlusMargin: z.boolean(),
  canViewCost: z.boolean(),
})

export type ProjectListAcl = ZodInfer<typeof ProjectListAclSchema>

export const ProjectListServiceModelSchema = z.object({
  acl: ProjectListAclSchema,
  items: z.array(ProjectListItemServiceModelSchema),
  // Add pagination metadata here (cursor, etc)
})

export type ProjectListServiceModel = ZodInfer<
  typeof ProjectListServiceModelSchema
>

export const ProjectListOrderByColumnSchema =
  ProjectDomainQueryModelSchema.keyof()
    .extract([
      'status',
      'name',
      'brandCompanyName',
      'lastTouchedTimestamp',
      'dueDate',
      'cost',
      'costPlusMargin',
    ])
    .or(z.literal('package'))

export type ProjectListOrderByColumn = ZodInfer<
  typeof ProjectListOrderByColumnSchema
>

export const ProjectListOrderByDirectionSchema = z.enum(['asc', 'desc'])

export type ProjectListOrderByDirection = ZodInfer<
  typeof ProjectListOrderByDirectionSchema
>

export const ProjectListOrderBySchema = z.object({
  column: ProjectListOrderByColumnSchema,
  direction: ProjectListOrderByDirectionSchema,
})

export type ProjectListOrderBy = ZodInfer<typeof ProjectListOrderBySchema>

export const ProjectListWhereSchema = z.object({
  organizationId: OrganizationIdSchema.optional(),
  search: NonEmptyStringSchema().optional(),
  statuses: nonEmptyArraySchema(ProjectStatusSchema).optional(),
})

export type ProjectListWhere = ZodInfer<typeof ProjectListWhereSchema>
